import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { usePwaInstall } from '@/hooks/use-pwa-install';
import { cn } from '@/lib/utils';
import { ArrowDownToLine, Check, Download, ExternalLink, LoaderCircle, Share, Smartphone } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

type InstallAppButtonProps = {
  className?: string;
  onInstalled?: () => void;
  showWhenUnavailable?: boolean;
};

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function Benefit({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5 text-sm">
      <span className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
        <Check className="h-3.5 w-3.5" />
      </span>
      {children}
    </li>
  );
}

function ManualInstallInstructions() {
  const ios = isIosDevice();

  return (
    <div className="bg-muted/60 border-border rounded-2xl border p-4">
      <p className="mb-3 text-sm font-semibold">{ios ? 'No iPhone ou iPad' : 'Instale pelo menu do navegador'}</p>
      <ol className="space-y-3">
        <li className="flex gap-3 text-sm">
          <span className="bg-background border-border flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border font-semibold">
            1
          </span>
          <span className="pt-1.5">
            {ios ? (
              <>
                Toque em <strong>Compartilhar</strong> <Share className="inline h-4 w-4 align-text-bottom" />
              </>
            ) : (
              <>
                Abra o <strong>menu do navegador</strong> <span aria-hidden="true">⋮</span>
              </>
            )}
          </span>
        </li>
        <li className="flex gap-3 text-sm">
          <span className="bg-background border-border flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border font-semibold">
            2
          </span>
          <span className="pt-1.5">
            Escolha <strong>{ios ? 'Adicionar à Tela de Início' : 'Instalar app'}</strong>
          </span>
        </li>
        <li className="flex gap-3 text-sm">
          <span className="bg-background border-border flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border font-semibold">
            3
          </span>
          <span className="pt-1.5">
            Confirme tocando em <strong>{ios ? 'Adicionar' : 'Instalar'}</strong>
          </span>
        </li>
      </ol>
      {ios ? (
        <p className="text-muted-foreground mt-3 text-xs">No iPhone, abra esta página no Safari para ver essa opção.</p>
      ) : null}
    </div>
  );
}

function InstallAppDialog({ trigger, onInstalled }: { trigger: ReactNode; onInstalled?: () => void }) {
  const { canInstall, install } = usePwaInstall();
  const [open, setOpen] = useState(false);
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);

    try {
      const installed = await install();
      if (installed) {
        setOpen(false);
        toast.success('App instalado com sucesso');
        onInstalled?.();
      } else {
        toast.info('Instalação cancelada', { description: 'Você pode tentar novamente quando quiser.' });
      }
      setInstalling(false);
    } catch {
      toast.error('Não foi possível instalar o app', { description: 'Tente novamente ou use o menu do navegador.' });
      setInstalling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto p-0 sm:max-w-md sm:rounded-3xl">
        <div className="gradient-primary flex items-center gap-4 rounded-t-none p-6 pr-12 sm:rounded-t-3xl">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <Smartphone className="h-7 w-7 text-white" />
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl text-white">Leve o Vira Tempo com você</DialogTitle>
            <DialogDescription className="text-white/75">
              Acesse sua agenda como um app, direto da tela inicial.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 p-6">
          <ul className="space-y-2.5">
            <Benefit>Abre em tela cheia, sem abas do navegador</Benefit>
            <Benefit>Acesso rápido à agenda e aos atendimentos</Benefit>
            <Benefit>Não ocupa espaço como um app tradicional</Benefit>
          </ul>

          {canInstall ? (
            <div className="space-y-3">
              <Button
                className="h-12 w-full rounded-xl text-base"
                disabled={installing}
                onClick={() => void handleInstall()}
              >
                {installing ? (
                  <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <ArrowDownToLine className="mr-2 h-5 w-5" />
                )}
                {installing ? 'Abrindo instalação…' : 'Instalar agora'}
              </Button>
              <p className="text-muted-foreground text-center text-xs">É grátis e leva só alguns segundos.</p>
            </div>
          ) : (
            <ManualInstallInstructions />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InstallAppButton({ className, onInstalled }: InstallAppButtonProps) {
  const { isInstalled } = usePwaInstall();

  if (isInstalled) return null;

  return (
    <InstallAppDialog
      onInstalled={onInstalled}
      trigger={
        <Button
          type="button"
          variant="ghost"
          className={cn('text-sidebar-foreground hover:bg-sidebar-muted/90 w-full justify-start', className)}
        >
          <Download className="mr-2 h-4 w-4" />
          Instalar app
        </Button>
      }
    />
  );
}

export function InstallAppMenuItem({ onInstalled }: Pick<InstallAppButtonProps, 'onInstalled'>) {
  const { isInstalled } = usePwaInstall();

  if (isInstalled) return null;

  return (
    <InstallAppDialog
      onInstalled={onInstalled}
      trigger={
        <DropdownMenuItem className="cursor-pointer" onSelect={(event) => event.preventDefault()}>
          <ExternalLink className="mr-2 h-4 w-4" />
          Instalar app
        </DropdownMenuItem>
      }
    />
  );
}
